import {
  divWithStyle,
  vizPortalFontStyle
} from '@tableau/data-catalog-shared';
import { assertNever } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogType, DialogWidget, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { Query } from 'react-apollo';

import { Messages } from '../../../gen-src/Localize';
import { FieldDetailsFragment } from '../../../gen-src/queries/FieldDetailsFragment';
import { FieldDetailsQuery, FieldDetailsQueryVariables } from '../../../gen-src/queries/FieldDetailsQuery';
import { NonDatasourceFieldDetailsFragment } from '../../../gen-src/queries/NonDatasourceFieldDetailsFragment';
import { FieldDetailsQueryGql } from '../../queries/FieldDetailsQueryGql';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { renderQueryResult } from '../renderQueryResult';
import { FieldInlineDescriptionEditor } from './FieldInlineDescriptionEditor';
import {
  ColumnsReferencingField,
  ConnectedField,
  FieldPropertyAggregation,
  FieldPropertyAggregationParam,
  FieldPropertyBinSize,
  FieldPropertyCombinationType,
  FieldPropertyDataCategory,
  FieldPropertyDataType,
  FieldPropertyDefaultFormat,
  FieldPropertyDelimiter,
  FieldPropertyDescription,
  FieldPropertyFolderName,
  FieldPropertyFormula,
  FieldPropertyHasOther,
  FieldPropertyHasUserReference,
  FieldPropertyIsAutoGenerated,
  FieldPropertyIsHidden,
  FieldPropertyRole,
  FieldPropertySemanticRole,
  FieldsReferencedByField,
} from './FieldPropertyComponents';

const dialogWidth = 500;

export const FieldDetailsPane = divWithStyle('FieldDetailsPane', {
  ...vizPortalFontStyle,
  fontSize: TabStyles.Typography.Sizing.Default,
});

export const FieldDetailsFieldName = divWithStyle('FieldDetailsFieldName', {
  fontSize: TabStyles.Typography.Sizing.XLarge,
});

export const FieldDetailsTypeRow = divWithStyle('FieldDetailsTypeRow', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

export const FieldDetailsProperties = divWithStyle('FieldDetailsProperties', {
  display: 'table',
  paddingTop: TabStyles.Spacing.DefaultSpace,
});


export interface FieldDetailsDialogProps {
  readonly fieldId: string;
  readonly makeFieldUrl: (fieldId: string) => string;
  readonly onClose: () => void;
  readonly canEditDescription: boolean;
  readonly datasourceLuid?: string;
  readonly afterDescriptionChange?: () => void;
}

export class FieldDetailsDialog extends React.Component<FieldDetailsDialogProps> {

  public render(): JSX.Element {
    return (
      <Query<FieldDetailsQuery, FieldDetailsQueryVariables>
        query={FieldDetailsQueryGql}
        variables={{ fieldId: this.props.fieldId }}
      >
        {result => renderQueryResult(result, { renderData: this.renderFieldDetails })}
      </Query>
    );
  }

  private readonly renderFieldDetails = (data: FieldDetailsQuery): React.ReactNode => {
    const field = data.fields && data.fields[0];
    if (!field) {
      return null;
    }
    const fieldIcon = iconForField(field);

    return (
      <DialogWidget
        testId='field-details'
        title={Messages.FieldDetails()}
        type={DialogType.NormalWithTitleBar}
        positioningBehavior={DialogPositioningBehavior.Centered}
        dialogWidth={dialogWidth}
        showCloseButton={true}
        showFooter={false}
        isModal={true}
        onCloseRequest={this.props.onClose}
        handleGlassClick={this.props.onClose}
      >
        <FieldDetailsPane>
          <FieldDetailsFieldName>{field.name}</FieldDetailsFieldName>
          <FieldDetailsTypeRow>
            <SvgFieldIcon fieldIcon={fieldIcon} />
            {fieldIcon ? FieldNameMappings[fieldIcon]() : null}
          </FieldDetailsTypeRow>
          <FieldDetailsProperties>
            {this.renderDescriptions(field, this.props.canEditDescription, this.props.datasourceLuid)}
            <FieldPropertyFolderName folderName={field.folderName} />
            <FieldPropertyIsHidden isHidden={field.isHidden} />
            {this.renderTypeSpecificFieldDetails(field)}
            {this.renderRelatedItems(field)}
          </FieldDetailsProperties>
        </FieldDetailsPane>
      </DialogWidget>
    );
  }

  private renderDescriptions(field: FieldDetailsFragment, canEditDescription: boolean, datasourceLuid?: string): React.ReactNode {
    if (field.__typename === 'DatasourceField' && field.remoteField) {  // workbook field descriptions cannot currently be edited
      return (
        <>
          <FieldPropertyDescription labelFunc={Messages.DescriptionWorkbook} description={field.description} />
          <FieldPropertyDescription labelFunc={Messages.DescriptionDataSource} description={field.remoteField.description} />
        </>
      );
    } else {
      return (
        <FieldInlineDescriptionEditor
          canEditDescription={canEditDescription}
          description={field.description || ''}
          datasourceLuid={datasourceLuid || ''}
          fullyQualifiedFieldName={field.fullyQualifiedName || ''}
          fieldType={field.__typename}
          afterDescriptionChange={this.props.afterDescriptionChange}
        />
      );
    }
  }

  private renderTypeSpecificFieldDetails(field: FieldDetailsFragment): React.ReactNode {
    if (field.__typename !== 'DatasourceField') {
      return renderTypeSpecificNonDatasourceFieldDetails(field, this.props.makeFieldUrl);
    } else if (field.remoteField) {
      return renderTypeSpecificNonDatasourceFieldDetails(field.remoteField, this.props.makeFieldUrl);
    } else {
      return null;
    }
  }

  private renderRelatedItems(field: FieldDetailsFragment): React.ReactNode {
    if (field.__typename === 'ColumnField') {
      const count = field.columnsConnection ? field.columnsConnection.totalCount : null;
      return <ColumnsReferencingField fieldId={field.id} rowCount={count} />;
    } else if (field.__typename === 'DatasourceField') {
      const remoteField = field.remoteField;
      if (remoteField) {
        const datasource = remoteField.datasource;
        const datasourceVizPortalUrlId = datasource && datasource.__typename === 'PublishedDatasource' ? datasource.vizportalUrlId : null;
        const datasourceName = datasource ? datasource.name : null;
        return (
          <ConnectedField
            fieldId={remoteField.id}
            fieldName={remoteField.name}
            datasourceVizPortalUrlId={datasourceVizPortalUrlId}
            datasourceName={datasourceName}
          />
        );
      } else {
        return null;
      }
    } else {
      const count = field.fieldsConnection ? field.fieldsConnection.totalCount : null;
      return <FieldsReferencedByField fieldId={field.id} rowCount={count} />;
    }
  }
}

export function renderTypeSpecificNonDatasourceFieldDetails(
  field: NonDatasourceFieldDetailsFragment,
  makeFieldUrl: (fieldId: string) => string
): React.ReactNode {
  switch (field.__typename) {
    case 'BinField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyFormula
            formula={field.formula}
            fields={field.fields}
            parameters={field.parameters}
            makeFieldUrl={makeFieldUrl}
          />
          <FieldPropertyBinSize binSize={field.binSize} />
        </>
      );
    case 'CalculatedField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyAggregation aggregation={field.aggregation} />
          <FieldPropertyAggregationParam aggregationParam={field.aggregationParam} />
          <FieldPropertyDefaultFormat defaultFormat={field.defaultFormat} />
          <FieldPropertySemanticRole semanticRole={field.semanticRole} />
          <FieldPropertyFormula
            formula={field.formula}
            fields={field.fields}
            parameters={field.parameters}
            makeFieldUrl={makeFieldUrl}
          />
          <FieldPropertyHasUserReference hasUserReference={field.hasUserReference} />
          <FieldPropertyIsAutoGenerated isAutoGenerated={field.isAutoGenerated} />
        </>
      );
    case 'ColumnField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyAggregation aggregation={field.aggregation} />
          <FieldPropertyAggregationParam aggregationParam={field.aggregationParam} />
          <FieldPropertyDefaultFormat defaultFormat={field.defaultFormat} />
          <FieldPropertySemanticRole semanticRole={field.semanticRole} />
        </>
      );
    case 'CombinedField':
      return null;
    case 'CombinedSetField':
      return (
        <>
          <FieldPropertyCombinationType combinationType={field.combinationType} />
          <FieldPropertyDelimiter delimiter={field.delimiter} />
        </>
      );
    // We don't expect datasource fields here;
    // they're handled in renderTypeSpecificFieldDetails.
    case 'DatasourceField':
      return null;
    case 'GroupField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyHasOther hasOther={field.hasOther} />
        </>
      );
    case 'HierarchyField':
      return null;
    case 'SetField':
      return null;
    default:
      assertNever(field);
      return null;
  }
}
